/* eslint-disable react/no-array-index-key */
/* eslint-disable no-template-curly-in-string */
import { useContext, useState, useEffect, useRef } from 'react'
import { Form } from '@unform/web'
import { MaskedInput, Select, ButtonNext } from 'components/index'
import { UserContext } from 'context'
import { useFetch, useStorage } from 'hooks'
import * as Yup from 'yup'
import { setLocale } from 'yup'
import { clearString, optionsLevels } from 'helper/utils'
import validate from 'helper/validate.js'
import { Container } from './style'
import Document from '../../Components/Document'

interface IObj {
  [_key: string]: any
}

interface ValidationErrors {
  [key: string]: string
}

const selectOptions = {
  partners: [
    { label: 'Individual', value: 1 },
    { label: 'Até 5', value: 5 },
    { label: 'Até 10', value: 10 },
    { label: 'Até 50', value: 50 },
    { label: 'Até 100', value: 100 },
    { label: 'Até 1000', value: 1000 },
    { label: 'Mais de 1000', value: 9999 },
  ],
  enterpriseType: [
    { label: 'MEI', value: 'mei' },
    { label: 'LTDA', value: 'ltda' },
    { label: 'Sociedade Anônima', value: 'sa' },
    { label: 'Associação', value: 'association' },
    { label: 'Empresário Individual', value: 'me' },
  ],
  specialist: [],
  pixKeyOptions: [
    { label: 'CPF', value: 'CPF' },
    { label: 'CNPJ', value: 'CNPJ' },
    { label: 'E-mail', value: 'EMAIL' },
    { label: 'Telefone', value: 'PHONE' },
    { label: 'Aleatória', value: 'EVP' },
  ],
}

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

setLocale({
  mixed: {
    default: 'Não é válido',
  },
  string: {
    min: 'Mínimo de ${min} caracteres',
    max: 'Máximo de ${max} caracteres',
  },
})

let formData: any = new FormData()

const schemaNewPJ = (rule: boolean) => {
  const formPixValue = formData.get('pix')
  const parsedPixValue = JSON.parse(formPixValue)
  const pixKeyValue = parsedPixValue?.key
  const pixTypeValue = parsedPixValue?.type

  return Yup.object().shape({
    document: Yup.string()
      .min(18)
      .max(18)
      .test('is-valid-cpf', 'CPF inválido', (value, context) => {
        if (!value || value.trim() === '') return true

        if (!validate.numbers.cnpj(value)) {
          return context.createError({ message: 'CNPJ inválido' })
        }

        return true // CPF válido
      })
      .required('Campo obrigatório'),
    name: Yup.string().required('Campo obrigatório'),
    nickname: Yup.string(),
    email: Yup.string()
      .matches(emailRegex, 'Email não é válido')
      .required('Campo obrigatório'),
    cellphone: Yup.string().min(15).max(15).required('Campo obrigatório'),
    key: Yup.string().test('pix-key-required', function () {
      const { createError } = this // Usar o contexto de Yup
      if (pixTypeValue && !pixKeyValue) {
        return createError({ message: 'Chave PIX é obrigatória' })
      }
      if (
        pixTypeValue === 'CNPJ' &&
        pixKeyValue &&
        !validate.numbers.cnpj(pixKeyValue)
      ) {
        return createError({ message: 'CNPJ inválido' })
      }
      if (
        pixTypeValue === 'CPF' &&
        pixKeyValue &&
        !validate.numbers.cpf(pixKeyValue)
      ) {
        return createError({ message: 'CPF inválido' })
      }
      if (
        pixTypeValue === 'PHONE' &&
        pixKeyValue &&
        !validate.numbers.cellphonePix(pixKeyValue)
      ) {
        return createError({ message: 'Telefone inválido' })
      }
      if (
        pixTypeValue === 'EMAIL' &&
        pixKeyValue &&
        !validate.string.email(pixKeyValue)
      ) {
        return createError({ message: 'Email inválido' })
      }
      return true
    }),
    pixType: Yup.string().test('pix-key-type-required', function () {
      const { createError } = this // Usar o contexto de Yup

      if (!pixTypeValue && pixKeyValue) {
        return createError({ message: 'Tipo de chave é obrigatória' })
      }
      return true
    }),
    address: Yup.object().shape({
      postalCode: Yup.string().min(8),
      address: Yup.string(),
      number: Yup.string(),
      complement: Yup.string(),
      province: Yup.string(),
    }),
    pix: Yup.string(),
    incomingValue: Yup.string(),
    customData: Yup.string(),
    gestorId: Yup.string().when([], {
      is: () => !rule,
      then: Yup.string(),
      otherwise: Yup.string(),
    }),
  })
}

export default function PJ() {
  const { requestFn, feedback, setPage } = useContext(UserContext)
  const formRef = useRef<HTMLFormElement | any>(null)
  const [especialistas] = useStorage('Especialistas')
  const [colaboradores] = useStorage('Colaboradores')
  const [personalData, setPersonalData] = useState<any[]>()
  const [addressData, setAddressData] = useState<any[]>()
  const [documentList, setDocumentList] = useState<IObj[]>([])
  const [defaultGestor, setDefaultGestor] = useState({})
  const [pixType, setPixType] = useState('')
  const [user] = useStorage('User')
  const [pixMask, setPixMask] = useState('')
  const [formIsCreatingUser, setFormIsCreatingUser] = useState(true)
  const inputErrorInitialState = {
    cnpj: '',
    email: '',
    cell: '',
  }
  const loadingInputInitialState = {
    cnpj: false,
    email: false,
    cell: false,
  }
  const [inputLoading, setInputLoading] = useState(loadingInputInitialState)
  const [inputError, setInputError] = useState(inputErrorInitialState)
  const api = useFetch()

  function formDataToObject(form: any) {
    const object: any = {}
    form.forEach((value: string, key: any) => {
      if (key === 'address') return (object[key] = JSON.parse(value))
      object[key] = value
    })
    return object
  }

  const handleSubmit = async () => {
    if (inputError.cnpj || inputError.email || inputError.cell) return
    try {
      formRef.current.setErrors({})
      const formObject = formDataToObject(formData)

      await schemaNewPJ(optionsLevels[user.level] === 'SPECIALIST').validate(
        formObject,
        { abortEarly: false }
      )
      feedback.current = { title: 'Conta criada!', error: false }
      requestFn.current = async () => {
        try {
          // Code
          const doc = clearString(formData.get('document'))
          ;[...formData.entries()].forEach((entry) => {
            const [key, value] = entry
            if (!value || value === '--') return formData.delete(key)
            if (key === 'birthDate') {
              const [d, m, y] = value.split('/')
              formData.set(key, `${y}-${m}-${d}`)
            }
          })

          documentList.forEach((docr: any) => {
            if (!Array.isArray(docr)) return
            formData.append('documentList', docr[1], docr[0])
          })
          const r: any = await api.fetch(
            'post',
            formData,
            `/gestor/account/${doc}`,
            {
              'Content-type': 'multipart/form-data',
            }
          )
          formData = new FormData()

          return r
        } catch (err) {
          console.error(err)
          return err
        }
      }

      setPage((prev) => prev + 1)
    } catch (err) {
      console.error(err)
      const validationErrors: ValidationErrors = {}
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          if (error.path) {
            validationErrors[error.path] = error.message
          }
        })

        formRef.current.setErrors(validationErrors)
      }
    }
  }

  useEffect(() => {
    const doc = clearString(formData.get('document'))

    if (doc?.length < 14) return

    api.fetch('get', null, `/gestor/account/${doc}`).then((response) => {
      setFormIsCreatingUser(response.fail)
    })
  }, [formData.get('document')]) // Detecta quando o form esta atualizando um usuario ou regristrando para verificar campos pre existentes no back quando se esta apenas registrando

  const handleInputRequest = (
    key: string,
    inputValue: string,
    type: string | null
  ) => {
    // Inicia o loading para o campo específico
    setInputLoading((prevState) => ({
      ...prevState,
      [key]: true,
    }))

    // Define o endpoint com base no tipo de validação
    const cleanInput =
      type === null ? clearString(inputValue).trim() : inputValue.trim()

    const endpoint =
      type !== null
        ? `/gestor/account/${cleanInput}?type=${type}`
        : `/gestor/account/${cleanInput}`
    // Executa a chamada da API
    api.fetch('get', null, endpoint).then((res) => {
      // Para o loading
      setInputLoading((prevState) => ({
        ...prevState,
        [key]: false, // Atualiza o campo específico para `false`
      }))

      // Atualiza o erro com base na resposta da API
      setInputError((prevState) => ({
        ...prevState,
        [key]: !res.errors ? '' : `${res.errors.message}`,
      }))
    })
  } // Verifica se o campo prenchido ja existe no banco de dados

  useEffect(() => {
    formData = new FormData()
  }, [])

  const updateAddress = () => {
    const curData = formData.get('address')
      ? JSON.parse(formData.get('address'))
      : {}

    Object.entries(curData)?.forEach((entry) => {
      if (!entry) return
      const [key, value] = entry
      const elem = formRef.current.getFieldRef(`address.${key}`)

      elem.current.element.value = value || ''
    })

    if (!curData.postalCode) return

    api
      .fetch('get', null, `/app/get-address/${curData.postalCode}`)
      .then((r: any) => {
        if (r.fail) return

        Object.entries(r.data).forEach(([key, value]: any) => {
          const el = formRef.current.getFieldRef(`address.${key}`)

          if (el) el.current.element.value = value
          curData[key] = value
          formData.set('address', JSON.stringify(curData))
        })
      })
      .catch((err) => {
        console.error('err', err)
      })
  }

  function callFetch() {
    const doc = clearString(formData.get('document'))
    if (doc?.length < 14) return
    api.fetch('get', null, `/gestor/account/${doc}`).then((response) => {
      if (!response.data) return
      const d = response.data.data
      const [pix = {}] = d.account.pixList

      formData.set('customData', JSON.stringify(d.user?.customData))
      formData.set('address', JSON.stringify(d.address))
      formData.set('pix', JSON.stringify(pix))
      formData.set('cellphone', d.account.cellphone)
      formData.set('email', d.account.email)
      formData.set('gestorId', d.account.gestorId)
      formData.set('name', d.user.name)
      formData.set('nickname', d.user.nickname)
      formData.set('incomingValue', d.user.incomingValue)

      const colab = colaboradores.find((item: IObj) => {
        return item.id === d.account.gestorId
      })
      if (pix.type) {
        const selectedOption = selectOptions.pixKeyOptions.find(
          (ih) => ih.value === pix.type
        )
        setPixType(selectedOption ? selectedOption.value : '')
      }

      if (colab) {
        setDefaultGestor({ label: colab.name, value: colab.id })
      }
      // Fill inputs
      ;[...formData.entries()].forEach((entry) => {
        const [key, value] = entry
        if (key === 'address') return
        const elem = formRef.current.getFieldRef(key)
        if (!elem?.element) return
        elem.element.value = value
      })

      setDocumentList(d.account.documentList)
      updateAddress()
    })
  }

  function handleChange(key: string) {
    switch (key) {
      case 'spouse': {
        const curData = formData.get('spouse')
          ? JSON.parse(formData.get('spouse'))
          : {}

        return (e: any) => {
          curData[e.target.name] = e.target.value
          if (!curData[e.target.name]) return delete curData[e.target.name]
          formData.set('spouse', JSON.stringify(curData))
        }
      }
      case 'address': {
        const curData = formData.get('address')
          ? JSON.parse(formData.get('address'))
          : {}

        return (e: any) => {
          const { name, value } = e.target
          const trueName = name.split('.')[1]
          if (trueName === 'postalCode' && value.length === 0)
            return formData.delete('address')
          if (!value || value.length === 0) return delete curData[trueName]
          curData[trueName] = value
          if (!curData[trueName]) return delete curData[trueName]
          formData.set('address', JSON.stringify(curData))
          if (trueName === 'postalCode' && value.length === 9)
            return updateAddress()
        }
      }
      case 'pix': {
        const curData = formData.get('pix')
          ? JSON.parse(formData.get('pix'))
          : {}
        return (e: any) => {
          const { name, value } = e.target

          if (value) {
            curData[name] = value // Adiciona ou atualiza o valor
          } else {
            delete curData[name] // Se o valor estiver vazio, deleta a chave
          }
          if (Object.keys(curData).length) {
            formData.set('pix', JSON.stringify(curData)) // Atualiza o campo 'pix'
          } else {
            formData.delete('pix') // Se o objeto estiver vazio, deleta o campo 'pix'
          }
        }
      }
      default: {
        return (e: any) => {
          const { value } = e.target
          if (!value) formData.delete(key)
          else formData.set(key, value)
          if (key === 'document') callFetch()
        }
      }
    }
  }

  useEffect(() => {
    formData = new FormData()
    formData.set('gestorId', user.id)
    setDefaultGestor({ label: user.name, value: user.id })
    setPersonalData([
      // type, name, placeholder
      ['cnpj', 'document', 'CNPJ'],
      [null, 'name', 'Razão social'],
      [null, 'nickname', 'Nome fantasia'],
    ])

    setAddressData([
      // type, name, key, placeholder
      ['cep', 'postalCode', 'address.postalCode', 'CEP'],
      [null, 'province', 'address.province', 'Bairro'],
      [null, 'address', 'address.address', 'Endereço com rua'],
      [null, 'number', 'address.number', 'Número'],
      [null, 'complement', 'address.complement', 'Complemento'],
    ])
  }, [])

  return (
    <Container>
      <div>
        <Form ref={formRef} method="POST" onSubmit={() => null}>
          <div>
            <h4 className="pf-title">Dados da Empresa</h4>
            <div className="pf-container">
              {personalData?.map((d: any[], i: number) => {
                const [type, name, placeholder] = d
                return (
                  <MaskedInput
                    key={`per_${i}`}
                    handleChange={(e: any) => handleChange(name)(e)}
                    smallPad
                    type={type}
                    placeholder={placeholder}
                    defaultValue={(() => {
                      const value = formData.get(name)
                      if (name !== 'birthDate') return value
                      if (!value) return
                      const [y, m, dd] = value.split('-')
                      return `${dd}/${m}/${y}`
                    })()}
                    name={name}
                  />
                )
              })}
              <MaskedInput
                handleChange={handleChange('email')}
                smallPad
                loading={inputLoading.email}
                customError={inputError.email}
                onBlur={async () => {
                  const { email } = formDataToObject(formData)

                  const emailIsvalid = emailRegex.test(email?.trim())

                  if (emailIsvalid && formIsCreatingUser) {
                    handleInputRequest('email', email, 'email')
                  } else {
                    setInputError((prevState) => ({
                      ...prevState,
                      email: '',
                    }))
                  }
                }}
                type=""
                defaultValue={formDataToObject(formData).email}
                placeholder="E-mail"
                name="email"
              />
              <MaskedInput
                handleChange={handleChange('cellphone')}
                smallPad
                loading={inputLoading.cell}
                customError={inputError.cell}
                onBlur={async () => {
                  const { cellphone } = formDataToObject(formData)

                  if (cellphone?.length === 15 && formIsCreatingUser) {
                    handleInputRequest('cell', cellphone, 'cell')
                  } else {
                    setInputError((prevState) => ({
                      ...prevState,
                      cell: '',
                    }))
                  }
                }}
                defaultValue={formDataToObject(formData).cellphone}
                type="cell"
                placeholder="Telefone"
                name="cellphone"
              />
              <Select
                options={selectOptions.partners}
                name="customData"
                placeholder={(() => {
                  const v = selectOptions.partners.find((item: IObj) => {
                    const obj = JSON.parse(formData.get('customData'))
                    return item.value === obj?.partnersAmount
                  })
                  return v ? v.label : 'Número de sócios'
                })()}
                onChange={(e) => {
                  // if (!formData.get('customData')) return false
                  const obj = formData.get('customData')
                    ? JSON.parse(formData.get('customData'))
                    : {}
                  obj.partnersAmount = e.value
                  formData.set('customData', JSON.stringify(obj))
                }}
              />
              <Select
                options={selectOptions.enterpriseType}
                name="customData"
                placeholder={(() => {
                  const v = selectOptions.enterpriseType.find((item: IObj) => {
                    if (!formData.get('customData')) return false
                    const obj = JSON.parse(formData.get('customData'))

                    return item.value === obj.enterpriseType
                  })
                  return v ? v.label : 'Tipo de empresa'
                })()}
                onChange={(e) => {
                  const obj = formData.get('customData')
                    ? JSON.parse(formData.get('customData'))
                    : {}
                  obj.enterpriseType = e.value
                  formData.set('customData', JSON.stringify(obj))
                }}
              />
              <MaskedInput
                smallPad
                type="money"
                handleChange={(e: any) =>
                  handleChange('incomingValue')({ target: { value: e } })
                }
                placeholder="Faturamento mensal da empresa"
                name="incomingValue"
                defaultValue={formData.get('incomingValue')}
              />
            </div>

            <h4 className="pf-title">Endereço</h4>
            <div className="pf-container">
              {addressData?.map((d: any[], i: number) => {
                const [type, name, key, placeholder] = d
                return (
                  <MaskedInput
                    key={`addr_${i}`}
                    smallPad
                    type={type}
                    handleChange={(e: any) => handleChange('address')(e)}
                    placeholder={placeholder}
                    defaultValue={(() => {
                      const addr = formData.get('address')
                        ? JSON.parse(formData.get('address'))
                        : {}
                      return addr[name]
                    })()}
                    name={key}
                  />
                )
              })}
              <div className="review">
                <MaskedInput
                  type="text"
                  small
                  disabled
                  name="address.state"
                  placeholder="UF"
                  defaultValue={(() => {
                    const addr = formData.get('address')
                      ? JSON.parse(formData.get('address'))
                      : {}
                    return addr.state
                  })()}
                />
                <MaskedInput
                  type="text"
                  small
                  disabled
                  name="address.city"
                  placeholder="Cidade"
                  defaultValue={(() => {
                    const addr = formData.get('address')
                      ? JSON.parse(formData.get('address'))
                      : {}
                    return addr.city
                  })()}
                />
              </div>
            </div>

            <h4 className="pf-title">Dados bancários</h4>
            <div className="pf-container">
              <MaskedInput
                smallPad
                type="text"
                handleChange={(e: any) => handleChange('pix')(e)}
                placeholder="Banco"
                defaultValue={(() => {
                  const pix = formData.get('pix')
                    ? JSON.parse(formData.get('pix'))
                    : {}
                  return pix.institution
                })()}
                name="institution"
              />
              <Select
                isClearable
                options={selectOptions.pixKeyOptions}
                name="pixType"
                placeholder="Tipo de chave"
                onChange={(e) => {
                  handleChange('pix')({
                    target: { name: 'type', value: e?.value },
                  })
                  setPixType(e?.value)
                  if (e?.value === 'PHONE') {
                    setPixMask('cellWithInternational')
                  }
                  if (e?.value === 'CPF') {
                    setPixMask('cpf')
                  }
                  if (e?.value === 'CNPJ') {
                    setPixMask('cnpj')
                  }
                  if (e?.value === 'EMAIL' || e?.value === 'EVP' || !e?.value) {
                    setPixMask('')
                  }
                }}
                value={selectOptions.pixKeyOptions.find(
                  (option) => option.value === pixType
                )}
              />
              <MaskedInput
                type={pixMask}
                handleChange={(e: any) => {
                  handleChange('pix')({
                    target: { name: 'key', value: e.target.value },
                  })
                }}
                placeholder="Chave"
                defaultValue={(() => {
                  const pix = formData.get('pix')
                    ? JSON.parse(formData.get('pix'))
                    : {}
                  return pix.key
                })()}
                name="key"
              />
            </div>

            <h4 className="pf-title">Especialista</h4>
            <div className="pf-container">
              <Select
                disabled={optionsLevels[user.level] === 'SPECIALIST'}
                value={defaultGestor}
                options={especialistas}
                name="gestorId"
                placeholder="Selecionar especialista"
                onChange={(e) => {
                  formData.set('gestorId', e.value)
                  setDefaultGestor(e)
                }}
              />
            </div>

            <h4 className="pf-title">Produto</h4>
            <div className="review-img-container">
              <Document
                // pf
                isOnForm
                document={formData.get('document')}
                documentList={documentList}
                onClickRemoveFile={(fileName: string) => {
                  setDocumentList((old: any) =>
                    old.filter((ct: any) => ct[0] !== fileName)
                  )
                }}
                onChange={(_key: any, file: File, name: string) => {
                  setDocumentList((old: any) => [...old, [name, file]])
                }}
              />
            </div>
          </div>
        </Form>
        <ButtonNext isAdvance advancePage={() => handleSubmit()} />
      </div>
    </Container>
  )
}
